<template>
  <div class="child-chat-container">
    <div class="chat-header">
      <h2>小朋友的学习好伙伴</h2>
      <p>和我聊天，一起学习新知识吧！</p>
    </div>
    
    <!-- 语音和情感设置 - 移除 CosyVoice 配置选项 -->
    <div class="settings-panel">
      <div class="voice-settings">
        <!-- <el-checkbox v-model="useCosyVoice" :disabled="!isWebSpeechSupported">
          使用高质量语音 (CosyVoice)
        </el-checkbox> -->
        <el-checkbox v-model="showEmotionIndicators">
          显示情感指示器
        </el-checkbox>
      </div>
      <div class="voice-controls" v-if="isSpeaking">
        <el-button @click="stopSpeaking" size="small" type="danger">
          停止朗读
        </el-button>
      </div>
    </div>
    
    <!-- 会话列表 -->
    <div class="session-list">
      <el-button @click="createNewSession" size="small" type="primary">新建对话</el-button>
      <el-select v-model="currentSessionId" @change="loadSessionHistory" size="small" placeholder="选择会话">
        <el-option
          v-for="session in sessions"
          :key="session.id"
          :label="session.sessionName"
          :value="session.id">
        </el-option>
      </el-select>
    </div>
    
    <div class="chat-messages" ref="messagesContainer">
      <div v-for="(msg, index) in messages" :key="index" 
           :class="['message', msg.type]">
        <div class="message-content">
          {{ msg.content }}
          <span 
            v-if="showEmotionIndicators && msg.emotion" 
            :class="['emotion-indicator', `emotion-${msg.emotion.emotion}`]"
            :style="{ color: getEmotionColor(msg.emotion) }"
          >
            {{ getEmotionEmoji(msg.emotion.emotion) }}
          </span>
        </div>
        <div class="message-actions">
          <!-- 为AI消息添加语音播放按钮 -->
          <el-button 
            v-if="msg.type === 'ai' && msg.content.trim()" 
            size="small" 
            @click="speakMessage(index)"
            :disabled="isSpeaking && currentSpeakingMessageIndex !== index"
          >
            <i class="fas fa-volume-up"></i>
            {{ isSpeaking && currentSpeakingMessageIndex === index ? '播放中...' : '朗读' }}
          </el-button>
        </div>
        <div class="message-time">
          {{ formatTime(new Date().toISOString()) }}
        </div>
      </div>
    </div>
    
    <!-- 输入区域 -->
    <div class="chat-input">
      <el-input
        v-model="inputMessage"
        placeholder="输入您的消息..."
        @keyup.enter="sendMessage"
        clearable
      />
      <el-button 
        type="primary" 
        @click="sendMessage" 
        :disabled="!inputMessage.trim() || !currentSessionId"
        title="请选择会话后再发送消息"
      >
        发送
      </el-button>
      
      <!-- 语音输入按钮 -->
      <el-button 
        v-if="isAsrSupported"
        :type="isRecording ? 'danger' : 'warning'"
        @click="isRecording ? stopVoiceInput() : startVoiceInput()"
        :disabled="!currentSessionId"
        title="点击开始语音输入"
      >
        <i :class="isRecording ? 'fas fa-stop' : 'fas fa-microphone'"></i>
        {{ isRecording ? '停止录音' : '语音输入' }}
      </el-button>
      
      <div class="age-selector">
        <label>年龄:</label>
        <el-select v-model="childAge" size="small">
          <el-option :value="6" label="6岁" />
          <el-option :value="8" label="8岁" />
          <el-option :value="10" label="10岁" />
          <el-option :value="12" label="12岁" />
        </el-select>
      </div>
    </div>
    
    <div class="related-knowledge" v-if="relatedKnowledge">
      <h4>相关知识点:</h4>
      <el-tag 
        v-for="(point, index) in relatedKnowledge.split(', ')" 
        :key="index" 
        size="small" 
        style="margin-right: 8px; margin-bottom: 8px;">
        {{ point }}
      </el-tag>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { 
  getUserChatSessions, 
  createChatSession, 
  getChatHistory,
  integratedStreamChatWithVoiceInSession
} from '@/services/modules/childChat'
import type { ChatSession } from '@/services/modules/childChat'
import voiceService from '@/services/modules/voiceService'
import emotionService, { type EmotionAnalysis } from '@/services/modules/emotionService'
// 添加ASR服务导入
import asrService from '@/services/modules/asrService'

// 定义流式聊天块类型
interface ChildChatStreamChunk {
  text: string
  isComplete: boolean
}

const messages = ref<Array<{type: string, content: string, timestamp?: string, emotion?: EmotionAnalysis}>>([
  { type: 'ai', content: '你好呀！我是你的学习伙伴，很高兴见到你！' }
])

const inputMessage = ref('')
const relatedKnowledge = ref('')
const messagesContainer = ref<HTMLElement | null>(null)

// 添加情感指示器开关
const showEmotionIndicators = ref(true)

// 语音相关状态 - 移除 useCosyVoice 配置，始终使用 CosyVoice
// const isWebSpeechSupported = ref(voiceService.isWebSpeechSupported()) // 注释掉这行
// const useCosyVoice = ref(false) // 移除这行
const isSpeaking = ref(false)
const currentSpeakingMessageIndex = ref<number | null>(null)
const currentSessionId = ref<number | null>(null)
const childAge = ref(8)
const currentStreamingMessageIndex = ref<number | null>(null)
// 添加当前会话ID跟踪，用于确保消息流隔离
const currentProcessingSessionId = ref<number | null>(null)

// 添加ASR相关状态
const isRecording = ref(false)
const isAsrSupported = ref(asrService.isRecordingSupported())

// PCMPlayer 类实现
class PCMPlayer {
  audioCtx: AudioContext
  sampleRate: number
  channels: number
  nextStartTime: number
  bufferQueue: any[]
  isPlaying: boolean

  constructor(sampleRate = 24000, channels = 1) {
    // 使用类型断言处理 webkitAudioContext 不存在的 TypeScript 错误
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    this.audioCtx = new AudioContext();
    this.sampleRate = sampleRate;
    this.channels = channels;
    this.nextStartTime = 0;
    this.bufferQueue = [];
    this.isPlaying = false;
  }

  // 将 Base64 PCM 解码为 AudioBuffer
  async decodePCM(base64Data: string) {
    const binary = atob(base64Data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    
    // 假设 16bit 小端 PCM
    const samples = new Int16Array(bytes.buffer);
    const floatSamples = new Float32Array(samples.length);
    
    // 16bit 转 float (-1.0 到 1.0)
    for (let i = 0; i < samples.length; i++) {
      floatSamples[i] = samples[i] / 32768.0;
    }

    const audioBuffer = this.audioCtx.createBuffer(
      this.channels, 
      floatSamples.length / this.channels, 
      this.sampleRate
    );
    
    const channelData = audioBuffer.getChannelData(0);
    channelData.set(floatSamples);
    
    return audioBuffer;
  }

  async playChunk(base64Data: string) {
    const audioBuffer = await this.decodePCM(base64Data);
    
    const source = this.audioCtx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(this.audioCtx.destination);
    
    // 无缝衔接：精确计算下一个片段的开始时间
    const currentTime = this.audioCtx.currentTime;
    if (this.nextStartTime < currentTime) {
      this.nextStartTime = currentTime;
    }
    
    // 增加 0.05s 缓冲，避免掉帧
    source.start(this.nextStartTime + 0.05);
    this.nextStartTime += audioBuffer.duration;
  }

  // 重置播放状态
  reset() {
    this.nextStartTime = 0;
    this.bufferQueue = [];
    this.isPlaying = false;
  }

  // 停止所有播放
  stop() {
    if (this.audioCtx.state !== 'closed') {
      this.audioCtx.close();
    }
    this.reset();
  }
}

// 创建 PCMPlayer 实例
const pcmPlayer = new PCMPlayer(24000, 1);

// 音频数据回调处理函数 - 使用 PCMPlayer 直接播放
const onAudioChunk = (audioData: string) => {
  console.log('【集成流式】收到音频数据，使用 PCMPlayer 播放');
  
  // 使用 PCMPlayer 直接播放音频块
  pcmPlayer.playChunk(audioData).catch(error => {
    console.error('【集成流式】播放音频块失败:', error);
  });
};

// 流式聊天相关状态

// 多轮对话相关
const sessions = ref<ChatSession[]>([])

// 初始化
onMounted(() => {
  // 加载会话列表
  loadUserSessions();
  
  // 默认欢迎消息
  messages.value = [{ type: 'ai', content: '你好呀！我是你的学习伙伴，很高兴见到你！' }]
  relatedKnowledge.value = ''
  
  // 添加全局未处理Promise拒绝监听器
  window.addEventListener('unhandledrejection', (event) => {
    console.error('【DEBUG】未处理的Promise拒绝:', event.reason);
    event.preventDefault(); // 防止默认的错误处理
  });
});

// 在组件卸载时清理 PCMPlayer
window.addEventListener('beforeunload', () => {
  pcmPlayer.stop();
});
const loadUserSessions = async () => {
  try {
    const userSessions = await getUserChatSessions()
    sessions.value = userSessions
    
    // 如果有会话ID参数，加载会话历史
    const urlParams = new URLSearchParams(window.location.search)
    const sessionId = urlParams.get('sessionId')
    if (sessionId) {
      currentSessionId.value = Number(sessionId)
      await loadSessionHistory(Number(sessionId))
    }
  } catch (error) {
    console.warn('加载会话列表失败:', error)
  }
}

const loadSessionHistory = async (sessionId: number) => {
  try {
    const history = await getChatHistory(sessionId)
    messages.value = history.map(msg => ({
      type: msg.role === 'user' ? 'user' : 'ai',
      content: msg.content,
      timestamp: msg.createdAt
    }))
    scrollToBottom()
  } catch (error) {
    console.warn('加载会话历史失败:', error)
    messages.value = [{ type: 'ai', content: '你好呀！我是你的学习伙伴，很高兴见到你！' }]
  }
}

const createNewSession = async () => {
  try {
    const sessionName = `对话 ${new Date().toLocaleDateString()}`
    const response = await createChatSession(sessionName)
    currentSessionId.value = response.id
    await loadUserSessions()
    messages.value = [{ type: 'ai', content: '你好呀！我是你的学习伙伴，很高兴见到你！' }]
    relatedKnowledge.value = ''
    
    // 重置 PCMPlayer 状态
    pcmPlayer.reset();
  } catch (error) {
    ElMessage.error('创建新对话失败')
    console.error('创建会话错误:', error)
  }
}

// ASR相关方法
const startVoiceInput = async () => {
  if (!currentSessionId.value) {
    ElMessage.warning('请先选择或创建一个会话');
    return;
  }
  
  if (!isAsrSupported.value) {
    ElMessage.error('当前浏览器不支持语音识别功能');
    return;
  }
  
  try {
    isRecording.value = true;
    ElMessage.info('正在录音，请说话...');
    
    await asrService.startRecording(
      (result) => {
        // 处理ASR识别结果（现在由后端直接触发智能对话，这里只显示用户消息）
        if (result.text && result.text.trim()) {
          // 显示用户消息
          messages.value.push({
            type: 'user',
            content: result.text.trim()
          });
          scrollToBottom();
          ElMessage.success('语音识别完成');
        } else {
          ElMessage.warning('未识别到有效语音');
        }
        // 移除这里的 isRecording.value = false，让录音持续进行
        // 停止逻辑应该由用户手动触发或通过其他方式控制
      },
      (error) => {
        console.error('语音识别失败:', error);
        ElMessage.error(`语音识别失败: ${error}`);
        isRecording.value = false; // 错误时停止录音
      },
      currentSessionId.value, // 传递会话ID
      childAge.value, // 传递儿童年龄
      (response) => {
        // 处理智能对话响应（包括流式文本和音频）
        if (response.type === 'interrupt') {
          console.log('🛑 收到打断通知，立即停止语音播放');
          // 立即停止PCM播放器
          pcmPlayer.stop();
          // 重置播放状态
          isSpeaking.value = false;
          currentSpeakingMessageIndex.value = null;
          // 清除当前流式消息索引，准备接收新消息
          currentStreamingMessageIndex.value = null;
          currentProcessingSessionId.value = null;
          return;
        }
        if (response.type === 'text' && response.text) {
          // 流式文本处理
          if (messages.value.length === 0 || messages.value[messages.value.length - 1].type !== 'ai') {
            // 如果还没有AI消息，创建一个新的
            messages.value.push({
              type: 'ai',
              content: response.text
            });
          } else {
            // 追加到现有的AI消息
            const lastIndex = messages.value.length - 1;
            messages.value[lastIndex] = {
              ...messages.value[lastIndex],
              content: messages.value[lastIndex].content + response.text
            };
          }
          scrollToBottom();
        }
        if (response.type === 'complete') {
          console.log('智能对话完成');
          // 智能对话完成后可以停止录音
          // isRecording.value = false;
        }
        if (response.type === 'error') {
          ElMessage.error(response.message || '智能对话处理失败');
          isRecording.value = false; // 错误时停止录音
        }
      }
    );
    
    // 设置TTS音频数据回调
    asrService.onAudioData = (audioData: ArrayBuffer) => {
      console.log('🔊 收到TTS音频数据，长度:', audioData.byteLength);
      try {
        // 将ArrayBuffer转换为base64字符串
        const uint8Array = new Uint8Array(audioData);
        let binary = '';
        for (let i = 0; i < uint8Array.length; i++) {
          binary += String.fromCharCode(uint8Array[i]);
        }
        const base64Audio = btoa(binary);
        
        // 播放音频
        pcmPlayer.playChunk(base64Audio);
      } catch (error) {
        console.error('播放TTS音频失败:', error);
      }
    };
    
    // 不要在这里停止录音！startRecording只是建立连接，录音应该持续进行
    // 移除之前可能存在的自动停止逻辑
    
  } catch (error: any) {
    console.error('录音启动异常:', error);
    let errorMessage = error.message || '录音启动失败';
    // 提供更具体的错误提示
    if (errorMessage.includes('麦克风')) {
      errorMessage = '请检查麦克风权限和设备连接';
    } else if (errorMessage.includes('WebSocket')) {
      errorMessage = '语音识别服务连接失败，请检查网络或联系管理员';
    }
    ElMessage.error(errorMessage);
    // 移除AI占位符
    if (currentStreamingMessageIndex.value !== null) {
      messages.value.splice(currentStreamingMessageIndex.value, 1);
      currentStreamingMessageIndex.value = null;
    }
    isRecording.value = false; // 启动失败时停止
  }
}

const stopVoiceInput = () => {
  asrService.stopRecording();
  isRecording.value = false;
  ElMessage.success('录音已停止');
}

const sendMessage = async () => {
  if (!inputMessage.value.trim() || !currentSessionId.value) return
  
  const userMessage = inputMessage.value.trim()
  inputMessage.value = ''
  
  // 添加用户消息
  messages.value.push({
    type: 'user',
    content: userMessage
  })
  
  scrollToBottom()
  
  try {
    // 添加AI回复占位符
    messages.value.push({
      type: 'ai',
      content: ''
    })
    
    const aiMessageIndex = messages.value.length - 1
    currentStreamingMessageIndex.value = aiMessageIndex
    // 记录当前处理的会话ID
    currentProcessingSessionId.value = currentSessionId.value
    
    // 重置 PCMPlayer 状态以开始新的音频流
    pcmPlayer.reset();
    
    // 如果是文本输入，使用SSE方式
    // 如果是语音输入，已经在ASR WebSocket中处理了
    // 这里只处理文本输入
    await integratedStreamChatWithVoiceInSession(
      currentSessionId.value,
      {
        message: userMessage,
        childAge: childAge.value
      },
      (chunk: ChildChatStreamChunk) => {
        // 检查是否是当前会话的消息流
        if (currentProcessingSessionId.value !== currentSessionId.value) {
          console.log('【DEBUG】收到过期的消息流，跳过处理');
          return;
        }
        
        if (currentStreamingMessageIndex.value !== null && 
            currentStreamingMessageIndex.value >= 0 && 
            currentStreamingMessageIndex.value < messages.value.length) {
          messages.value[currentStreamingMessageIndex.value].content += chunk.text
          
          if (chunk.isComplete) {
            console.log('【DEBUG】收到文本 complete 事件');
            messages.value[currentStreamingMessageIndex.value].content = 
              messages.value[currentStreamingMessageIndex.value].content.trim()
            
            const emotion = emotionService.analyzeEmotion(messages.value[currentStreamingMessageIndex.value].content)
            messages.value[currentStreamingMessageIndex.value].emotion = emotion
          }
          
          scrollToBottom()
        }
      },
      onAudioChunk,
      () => {
        console.log('【DEBUG】收到 voice_complete 事件');
        currentStreamingMessageIndex.value = null;
        currentProcessingSessionId.value = null;
      },
      (error: string) => {
        ElMessage.error(error)
        if (currentStreamingMessageIndex.value !== null && 
            currentStreamingMessageIndex.value >= 0 && 
            currentStreamingMessageIndex.value < messages.value.length) {
          messages.value[currentStreamingMessageIndex.value].content = '抱歉，我现在有点忙，稍后再和你聊天好吗？'
        }
        currentStreamingMessageIndex.value = null;
        currentProcessingSessionId.value = null;
      }
    )
  } catch (error) {
    console.error('【DEBUG】发送消息发生异常:', error);
    ElMessage.error('发送消息失败，请重试')
    currentStreamingMessageIndex.value = null;
    currentProcessingSessionId.value = null;
  }
}

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

const formatTime = (timestamp: string) => {
  const date = new Date(timestamp)
  return date.toLocaleTimeString('zh-CN', { hour12: false })
}

// 语音相关方法
const speakMessage = async (index: number) => {
  // 添加边界检查
  if (index < 0 || index >= messages.value.length) {
    console.warn('Invalid message index for speech:', index)
    return
  }
  
  const message = messages.value[index]
  if (!message || !message.content || !message.content.trim()) {
    console.warn('Empty or invalid message content for speech')
    return
  }
  
  // 如果正在说话，先取消
  if (isSpeaking.value) {
    voiceService.cancel()
  }
  
  currentSpeakingMessageIndex.value = index
  isSpeaking.value = true
  
  try {
    // 始终使用 CosyVoice 方案
    await voiceService.speak(message.content, () => {
      isSpeaking.value = false
      currentSpeakingMessageIndex.value = null
    })
  } catch (error) {
    console.error('语音播放失败:', error)
    isSpeaking.value = false
    currentSpeakingMessageIndex.value = null
    ElMessage.error('语音功能暂时不可用，请稍后再试')
  }
}

const stopSpeaking = () => {
  voiceService.cancel()
  isSpeaking.value = false
  currentSpeakingMessageIndex.value = null
  // 停止 PCMPlayer
  pcmPlayer.stop();
}

// 情感显示相关
const getEmotionEmoji = (emotion: string) => {
  const emojis: Record<string, string> = {
    positive: '😊',
    negative: '😔',
    neutral: '😐',
    excited: '😄',
    sad: '😢',
    curious: '🤔',
    angry: '😠',
    happy: '😁'
  }
  
  return emojis[emotion] || '🙂'
}

const getEmotionColor = (emotion: EmotionAnalysis | undefined) => {
  if (!emotion) return '#999'
  
  const colors: Record<string, string> = {
    positive: '#409eff',
    negative: '#f56c6c',
    neutral: '#909399',
    excited: '#ff9900',
    sad: '#909399',
    curious: '#67c23a',
    angry: '#f56c6c',
    happy: '#409eff'
  }
  
  return colors[emotion.emotion] || '#999'
}

</script>

<style scoped>
.child-chat-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
}

.chat-header {
  text-align: center;
  margin-bottom: 20px;
}

.chat-header h2 {
  color: #409eff;
  margin-bottom: 8px;
}

.settings-panel {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 8px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.voice-settings {
  display: flex;
  gap: 16px;
}

.session-list {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  align-items: center;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background-color: #f9fafc;
}

.message {
  margin-bottom: 16px;
  padding: 12px;
  border-radius: 8px;
  max-width: 80%;
  position: relative;
}

.message.user {
  background-color: #409eff;
  color: white;
  margin-left: auto;
  text-align: right;
}

.message.ai {
  background-color: #f0f2f5;
  color: #333;
  margin-right: auto;
}

.message-content {
  margin-bottom: 8px;
}

.emotion-indicator {
  margin-left: 8px;
  font-size: 16px;
}

.message-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.message-time {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  text-align: right;
}

.chat-input {
  display: flex;
  gap: 16px;
  align-items: flex-end;
}

.age-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.age-selector label {
  font-weight: bold;
}

.related-knowledge {
  margin-top: 16px;
  padding: 16px;
  background-color: #f0f9ff;
  border-radius: 4px;
}

.related-knowledge h4 {
  margin-bottom: 8px;
  color: #409eff;
}
</style>